import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Button, Descriptions, Drawer, Form, Modal, Space, Tabs, Tag, Tooltip } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  RollbackOutlined,
  UserAddOutlined,
  CopyOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import moment from "moment";
import message from "@/utils/globalMessage";
import DeleteConfirm from "@components/Feedback/DeleteConfirm";
import PageHeader from "@components/Table/PageHeader";
import ResizableProTable from "@components/Table/ResizableProTable";
import ResponsiveSelect from "@components/ResponsiveSelect";

const PENDING_STATUSES = new Set(["pending_invited", "pending_applied"]);
const STATUS_LABELS = {
  pending_invited: "Invitation pending",
  pending_applied: "Application pending",
  active: "Active",
  rejected: "Rejected",
  revoked: "Revoked",
};
const STATUS_COLORS = {
  pending_invited: "gold",
  pending_applied: "processing",
  active: "success",
  rejected: "error",
  revoked: "default",
};

const getInitials = (value) => {
  const parts = String(value || "?").trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : parts[0] || "?")
    .slice(0, 2)
    .toUpperCase();
};

const formatDate = (value) => (value ? moment(value).format("YYYY-MM-DD HH:mm") : "-");

const sortRelationships = (rows) => [...(rows || [])].sort((a, b) => {
  const pendingDelta = Number(PENDING_STATUSES.has(b.status)) - Number(PENDING_STATUSES.has(a.status));
  if (pendingDelta) return pendingDelta;
  return new Date(b.modified_date || b.created_date || 0) - new Date(a.modified_date || a.created_date || 0);
});

export default function ContractCompanyRelationshipTable({
  mode,
  loadRelationships,
  onCreate,
  onCopyInvitationLink,
  onEmailInvite,
  onInviteExisting,
  canWrite = false,
  canFullAccess = false,
  onAccept,
  onReject,
  onWithdraw,
  onApprove,
  onRemove,
  detailPath,
  title,
  companyIds = [],
  companyOptions = [],
  onCompanyChange,
}) {
  const isContractSide = mode === "contract";
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const actionRef = useRef();
  const [detail, setDetail] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const request = useCallback(async () => {
    try {
      const response = await loadRelationships({ company: companyIds });
      const payload = response?.data?.results || response?.data || response || [];
      return { data: sortRelationships(Array.isArray(payload) ? payload : []), total: Array.isArray(payload) ? payload.length : 0, success: true };
    } catch (_) {
      message.error("Failed to load company relationships");
      return { data: [], total: 0, success: false };
    }
  }, [companyIds, loadRelationships]);

  useEffect(() => {
    actionRef.current?.reload?.();
  }, [companyIds]);

  const refresh = useCallback(async (operation) => {
    try {
      await operation();
      actionRef.current?.reload?.();
    } catch (error) {
      message.error(error?.response?.data?.detail || Object.values(error?.response?.data || {})[0] || "Unable to update relationship");
    }
  }, []);

  const canRemove = Boolean(onRemove && (isContractSide || canFullAccess));
  const handleSelectionChange = useCallback((keys, rows) => {
    setSelectedRowKeys(keys);
    setSelectedRows((previousRows) => {
      const rowMap = new Map();
      [...previousRows, ...(rows || [])].forEach((row) => {
        if (row?.id != null) rowMap.set(row.id, row);
      });
      return keys.map((key) => rowMap.get(key)).filter(Boolean);
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (!canRemove || !selectedRowKeys.length) return;
    try {
      setDeleting(true);
      const rows = selectedRows.length ? selectedRows : selectedRowKeys.map((id) => ({ id }));
      await Promise.all(rows.map((row) => onRemove(row.id)));
      message.success("Deleted successfully");
      clearSelection();
      actionRef.current?.reload?.();
    } catch (error) {
      message.error(error?.response?.data?.detail || "Unable to delete selected relationships");
    } finally {
      setDeleting(false);
    }
  }, [canRemove, clearSelection, onRemove, selectedRowKeys, selectedRows]);

  const renderBulkActionsBar = useCallback(() => {
    if (!canRemove || !selectedRowKeys.length) return null;
    return (
      <div className="table-bulk-floating-bar" role="toolbar" aria-label="Bulk actions for selected rows">
        <Space size={10} align="center" wrap className="table-bulk-floating-bar-inner">
          <span className="table-bulk-floating-bar-count">
            <span className="table-bulk-floating-bar-count-label">Selected</span>
            <span className="table-bulk-floating-bar-count-num">{selectedRowKeys.length}</span>
            <Tooltip title="Clear selection">
              <Button
                type="text"
                size="small"
                className="table-bulk-floating-bar-clear"
                icon={<CloseOutlined />}
                onClick={clearSelection}
                aria-label="Clear selection"
              />
            </Tooltip>
          </span>
          <Tooltip title="Delete selected">
            <DeleteConfirm
              title="Delete the selected relationships?"
              description="This action cannot be undone!"
              onConfirm={handleBulkDelete}
              zIndex={1500}
            >
              <Button
                type="default"
                danger
                shape="circle"
                className="table-bulk-floating-btn"
                icon={<DeleteOutlined />}
                loading={deleting}
                aria-label="Delete selected"
              />
            </DeleteConfirm>
          </Tooltip>
        </Space>
      </div>
    );
  }, [canRemove, clearSelection, deleting, handleBulkDelete, selectedRowKeys.length]);

  const detailContent = detail ? (
    <Tabs defaultActiveKey="overview" destroyInactiveTabPane>
      <Tabs.TabPane tab="Overview" key="overview">
        <Descriptions column={1} size="middle" bordered>
          <Descriptions.Item label="Name"><Space><Avatar src={isContractSide ? detail.company_logo : detail.contract_company_logo}>{getInitials(isContractSide ? detail.company_name : detail.contract_company_name)}</Avatar>{isContractSide ? detail.company_name : detail.contract_company_name}</Space></Descriptions.Item>
          <Descriptions.Item label="Email">{detail.contract_company_email || detail.company_email || "-"}</Descriptions.Item>
          <Descriptions.Item label="Status"><Tag color={STATUS_COLORS[detail.status]}>{STATUS_LABELS[detail.status] || detail.status}</Tag></Descriptions.Item>
          <Descriptions.Item label="Created">{formatDate(detail.created_date)}</Descriptions.Item>
          <Descriptions.Item label="Last updated">{formatDate(detail.modified_date)}</Descriptions.Item>
        </Descriptions>
      </Tabs.TabPane>
      <Tabs.TabPane tab="Service categories" key="service-categories">
        {(detail.service_categories || []).length ? (
          <Space wrap size={[8, 10]} style={{ width: "100%" }}>
            {(detail.service_categories || []).map((item) => <Tag key={item.id || item.name} color="blue">{item.name}</Tag>)}
          </Space>
        ) : <span style={{ color: "#8c8c8c" }}>No service categories</span>}
      </Tabs.TabPane>
    </Tabs>
  ) : null;

  const openDetail = useCallback((record) => {
    if (detailPath) {
      detailPath(record);
      return;
    }
    setDetail(record);
  }, [detailPath]);

  const columns = useMemo(() => {
    const nameKey = isContractSide ? "company_name" : "contract_company_name";
    const logoKey = isContractSide ? "company_logo" : "contract_company_logo";
    return [
      {
        title: isContractSide ? "Company" : "Contractor",
        dataIndex: nameKey,
        key: nameKey,
        width: 260,
        render: (value, record) => (
          <a
            href="#details"
            onClick={(event) => { event.preventDefault(); openDetail(record); }}
            style={{ color: "#1677ff", textDecoration: "underline" }}
          >
            <Space size={8}>
              <Avatar size={30} src={record[logoKey]}>{getInitials(value)}</Avatar>
              <span>{value || "-"}</span>
            </Space>
          </a>
        ),
      },
      {
        title: "Email",
        dataIndex: "contract_company_email",
        key: "contract_company_email",
        width: 230,
        render: (value, record) => value || record.company_email || "-",
      },
      {
        title: "Service categories",
        key: "service_categories",
        width: 240,
        render: (_, record) => {
          const categories = (record.service_categories || []).filter((item) => item?.name);
          if (!categories.length) return "-";
          const categoryNames = categories.map((item) => item.name);
          return (
            <Tooltip title={categoryNames.join(", ")}>
              <Space size={[4, 4]} wrap>
                {categories.map((item) => <Tag key={item.id || item.name} color="blue">{item.name}</Tag>)}
              </Space>
            </Tooltip>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 170,
        render: (value) => <Tag color={STATUS_COLORS[value]}>{STATUS_LABELS[value] || value || "-"}</Tag>,
      },
      {
        title: "Last updated",
        dataIndex: "modified_date",
        key: "modified_date",
        width: 170,
        sorter: true,
        render: formatDate,
      },
      {
        title: "Actions",
        key: "actions",
        valueType: "option",
        fixed: "right",
        width: 220,
        render: (_, record) => (
          <Space size={4} wrap>
            {isContractSide && record.status === "pending_invited" ? <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => refresh(() => onAccept(record.id))}>Accept</Button> : null}
            {isContractSide && record.status === "pending_invited" ? <Button size="small" icon={<CloseOutlined />} onClick={() => refresh(() => onReject(record.id))}>Reject</Button> : null}
            {isContractSide && record.status === "pending_applied" ? <Button size="small" icon={<RollbackOutlined />} onClick={() => refresh(() => onWithdraw(record.id))}>Withdraw</Button> : null}
            {!isContractSide && record.status === "pending_applied" && canWrite ? <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => refresh(() => onApprove(record.id))}>Approve</Button> : null}
            {!isContractSide && record.status === "pending_applied" && canWrite ? <Button size="small" icon={<CloseOutlined />} onClick={() => refresh(() => onReject(record.id))}>Reject</Button> : null}
            {record.status !== "revoked" && canRemove ? <Button danger size="small" icon={<DeleteOutlined />} onClick={() => refresh(() => onRemove(record.id))}>Remove</Button> : null}
          </Space>
        ),
      },
    ];
  }, [canFullAccess, canRemove, canWrite, isContractSide, onAccept, onApprove, onReject, onRemove, onWithdraw, openDetail, refresh]);

  const toolbar = (
    <PageHeader
      hide_imports
      hide_search
      hid_new
      handleFresh={() => actionRef.current?.reload?.()}
      renderSearches={() => companyOptions.length ? (
        <Form className="filter-form contract-filter-form" autoComplete="off">
          <Form.Item label="Company" name="company">
            <ResponsiveSelect
              mode="multiple"
              allowClear
              showSearch
              popupTitle="Select Companies"
              placeholder="Company"
              value={companyIds}
              options={companyOptions.map((item) => ({ value: item.id, label: item.name }))}
              onChange={(value) => onCompanyChange?.(value || [])}
            />
          </Form.Item>
        </Form>
      ) : null}
      resetFilters={() => onCompanyChange?.([])}
      filterHasValue={Boolean(companyIds.length)}
      renderButtons={() => (
        <Space size={8}>
          {(isContractSide ? onCreate : onCopyInvitationLink || onEmailInvite || onInviteExisting) ? (
            isContractSide ? (
              <Tooltip title="Apply to company">
                <Button type="primary" shape={isPC ? "default" : "circle"} icon={<UserAddOutlined />} onClick={() => onCreate(() => actionRef.current?.reload?.())} aria-label="Apply to company">
                  {isPC ? "Apply to company" : null}
                </Button>
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Copy invitation link"><Button shape={isPC ? "default" : "circle"} icon={<CopyOutlined />} onClick={onCopyInvitationLink} aria-label="Copy invitation link">{isPC ? "Copy invitation link" : null}</Button></Tooltip>
                <Tooltip title="Email invitation"><Button shape={isPC ? "default" : "circle"} icon={<MailOutlined />} onClick={() => onEmailInvite?.(() => actionRef.current?.reload?.())} aria-label="Email invitation">{isPC ? "Email invitation" : null}</Button></Tooltip>
                <Tooltip title="Invite existing contractor"><Button type="primary" shape={isPC ? "default" : "circle"} icon={<UserAddOutlined />} onClick={() => onInviteExisting?.(() => actionRef.current?.reload?.())} aria-label="Invite existing contractor">{isPC ? "Invite existing contractor" : null}</Button></Tooltip>
              </>
            )
          ) : null}
        </Space>
      )}
    />
  );

  return (
    <div style={{ width: "100%", minWidth: 0, padding: isPC ? 16 : 8 }}>
      <ResizableProTable
        actionRef={actionRef}
        headerTitle={title}
        rowKey="id"
        columns={columns}
        request={request}
        search={false}
        options={false}
        rowSelection={{ selectedRowKeys, onChange: handleSelectionChange, preserveSelectedRowKeys: true }}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        scroll={{ x: isContractSide ? 1100 : 900 }}
        toolBarRender={() => toolbar}
        mobileTitleOnClick={openDetail}
        locale={{ emptyText: isContractSide ? "No company invitations or applications" : "No contractor relationships" }}
      />
      {renderBulkActionsBar()}
      {isPC ? (
        <Modal open={Boolean(detail)} title={detail ? (isContractSide ? detail.company_name : detail.contract_company_name) : "Details"} onCancel={() => setDetail(null)} footer={null} width={560} bodyStyle={{ paddingTop: 8 }}>
          {detailContent}
        </Modal>
      ) : (
        <Drawer placement="bottom" height="78vh" visible={Boolean(detail)} title={detail ? (isContractSide ? detail.company_name : detail.contract_company_name) : "Details"} onClose={() => setDetail(null)} destroyOnClose={false} className="contract-relationship-detail-drawer">
          {detailContent}
        </Drawer>
      )}
    </div>
  );
}
